import {
  infiniteQueryOptions,
  keepPreviousData,
  QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { InfiniteData, QueryKey } from "@tanstack/react-query";
import { useCallback } from "react";
import type {
  CloudflareIdentityProvider,
  CloudflareMcpServerSyncResult,
  CloudflareScimConnection,
  CloudflareServiceToken,
  DirectoryUser,
  OidcApplication,
  SecurityEventOutcome,
  SecurityEventSource,
} from "../shared/types";
import { api, ApiError } from "./api";
import type {
  AdminIdentity,
  CloudflareAccessGroupsPage,
  CloudflareDevicePostureView,
  CloudflareDevicesPage,
  CloudflareMcpPortalsPage,
  CloudflareMcpServersPage,
  CloudflareReusablePoliciesPage,
  CloudflareZeroTrustListsView,
  CloudflareOverview,
  PasswordPolicy,
  ProviderView,
  SecurityEventsPage,
} from "./types";

export interface SecurityEventQueryFilters {
  actor?: string;
  source?: SecurityEventSource;
  outcome?: SecurityEventOutcome;
  since?: number;
}

export interface CloudflareDeviceQueryFilters {
  search?: string;
  status?: "all" | "active" | "revoked";
}

export const adminQueryKeys = {
  session: ["admin", "session"] as const,
  users: ["admin", "users"] as const,
  applications: ["admin", "applications"] as const,
  provider: ["admin", "provider"] as const,
  security: ["admin", "security-events"] as const,
  securityLatest: ["admin", "security-events", "latest"] as const,
  securityInfinite: (limit: number, filters: SecurityEventQueryFilters = {}) =>
    ["admin", "security-events", "infinite", { limit, ...filters }] as const,
  cloudflare: ["admin", "cloudflare-overview"] as const,
  cloudflareDevices: ["admin", "cloudflare-devices"] as const,
  cloudflareDevicesInfinite: (
    limit: number,
    filters: CloudflareDeviceQueryFilters = {},
  ) =>
    ["admin", "cloudflare-devices", "infinite", { limit, ...filters }] as const,
  cloudflareDevicePosture: ["admin", "cloudflare-device-posture"] as const,
  cloudflareMcpPortals: ["admin", "cloudflare-mcp-portals"] as const,
  cloudflareMcpPortalsInfinite: (limit: number, search?: string) =>
    [
      "admin",
      "cloudflare-mcp-portals",
      "infinite",
      { limit, ...(search ? { search } : {}) },
    ] as const,
  cloudflareMcpServers: ["admin", "cloudflare-mcp-servers"] as const,
  cloudflareMcpServersInfinite: (limit: number, search?: string) =>
    [
      "admin",
      "cloudflare-mcp-servers",
      "infinite",
      { limit, ...(search ? { search } : {}) },
    ] as const,
  cloudflareReusablePolicies: [
    "admin",
    "cloudflare-reusable-policies",
  ] as const,
  cloudflareReusablePoliciesInfinite: (limit: number) =>
    ["admin", "cloudflare-reusable-policies", "infinite", { limit }] as const,
  cloudflareAccessGroups: ["admin", "cloudflare-access-groups"] as const,
  cloudflareAccessGroupsInfinite: (limit: number, search?: string) =>
    [
      "admin",
      "cloudflare-access-groups",
      "infinite",
      { limit, ...(search ? { search } : {}) },
    ] as const,
  cloudflareZeroTrustLists: ["admin", "cloudflare-zero-trust-lists"] as const,
};

export type AdminView =
  "overview" | "applications" | "users" | "cloudflare" | "provider" | "audit";

export type AdminDataQuery =
  | "users"
  | "applications"
  | "provider"
  | "security"
  | "securityTimeline"
  | "cloudflare";

export function queriesForAdminView(view: AdminView): AdminDataQuery[] {
  if (view === "overview") {
    return ["users", "applications", "provider", "security"];
  }
  if (view === "audit") return ["securityTimeline"];
  return [view];
}

function retryAdminQuery(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < 1;
}

export interface AdminSession {
  user: AdminIdentity;
  csrfToken: string;
  passwordPolicy: PasswordPolicy;
}

export const adminQueries = {
  session: () =>
    queryOptions({
      queryKey: adminQueryKeys.session,
      queryFn: ({ signal }) =>
        api<AdminSession>("/api/admin/session", { signal }),
      retry: false,
      staleTime: Number.POSITIVE_INFINITY,
    }),
  users: () =>
    queryOptions({
      queryKey: adminQueryKeys.users,
      queryFn: ({ signal }) =>
        api<{ users: DirectoryUser[] }>("/api/admin/users", { signal }),
    }),
  applications: () =>
    queryOptions({
      queryKey: adminQueryKeys.applications,
      queryFn: ({ signal }) =>
        api<{ applications: OidcApplication[] }>("/api/admin/applications", {
          signal,
        }),
    }),
  provider: () =>
    queryOptions({
      queryKey: adminQueryKeys.provider,
      queryFn: ({ signal }) =>
        api<{ provider: ProviderView }>("/api/admin/provider", { signal }),
      staleTime: 5 * 60 * 1000,
    }),
  security: () =>
    queryOptions({
      queryKey: adminQueryKeys.securityLatest,
      queryFn: ({ signal }) =>
        api<SecurityEventsPage>("/api/admin/security/events?limit=25", {
          signal,
        }),
      staleTime: 15 * 1000,
    }),
  securityInfinite: (limit = 25, filters: SecurityEventQueryFilters = {}) =>
    infiniteQueryOptions({
      queryKey: adminQueryKeys.securityInfinite(limit, filters),
      queryFn: ({ signal, pageParam }) => {
        const query = new URLSearchParams({ limit: String(limit) });
        if (pageParam) query.set("cursor", pageParam);
        if (filters.actor) query.set("actor", filters.actor);
        if (filters.source) query.set("source", filters.source);
        if (filters.outcome) query.set("outcome", filters.outcome);
        if (filters.since !== undefined) {
          query.set("since", String(filters.since));
        }
        return api<SecurityEventsPage>(
          `/api/admin/security/events?${query.toString()}`,
          { signal },
        );
      },
      initialPageParam: "",
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      getPreviousPageParam: () => undefined,
      maxPages: 20,
      placeholderData: keepPreviousData,
      staleTime: 15 * 1000,
    }),
  cloudflare: () =>
    queryOptions({
      queryKey: adminQueryKeys.cloudflare,
      queryFn: ({ signal }) =>
        api<CloudflareOverview>("/api/admin/cloudflare/overview", { signal }),
      staleTime: 15 * 1000,
    }),
  cloudflareDevices: (limit = 25, filters: CloudflareDeviceQueryFilters = {}) =>
    infiniteQueryOptions({
      queryKey: adminQueryKeys.cloudflareDevicesInfinite(limit, filters),
      queryFn: ({ signal, pageParam }) => {
        const query = new URLSearchParams({ limit: String(limit) });
        if (pageParam) query.set("cursor", pageParam);
        if (filters.search) query.set("search", filters.search);
        if (filters.status) query.set("status", filters.status);
        return api<CloudflareDevicesPage>(
          `/api/admin/cloudflare/devices?${query.toString()}`,
          { signal },
        );
      },
      initialPageParam: "",
      getNextPageParam: (lastPage) => lastPage.nextCursor,
      getPreviousPageParam: () => undefined,
      maxPages: 20,
      staleTime: 15 * 1000,
    }),
  cloudflareDevicePosture: () =>
    queryOptions({
      queryKey: adminQueryKeys.cloudflareDevicePosture,
      queryFn: ({ signal }) =>
        api<CloudflareDevicePostureView>(
          "/api/admin/cloudflare/device-posture",
          { signal },
        ),
      staleTime: 60 * 1000,
    }),
  cloudflareMcpPortals: (limit = 20, search?: string) =>
    infiniteQueryOptions({
      queryKey: adminQueryKeys.cloudflareMcpPortalsInfinite(limit, search),
      queryFn: ({ signal, pageParam }) => {
        const query = new URLSearchParams({
          page: String(pageParam),
          limit: String(limit),
        });
        if (search) query.set("search", search);
        return api<CloudflareMcpPortalsPage>(
          `/api/admin/cloudflare/mcp/portals?${query.toString()}`,
          { signal },
        );
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        typeof lastPage.totalPages === "number"
          ? lastPage.page < lastPage.totalPages
            ? lastPage.page + 1
            : undefined
          : lastPage.portals.length === limit
            ? lastPage.page + 1
            : undefined,
      getPreviousPageParam: () => undefined,
      maxPages: 20,
      staleTime: 30 * 1000,
    }),
  cloudflareMcpServers: (limit = 20, search?: string) =>
    infiniteQueryOptions({
      queryKey: adminQueryKeys.cloudflareMcpServersInfinite(limit, search),
      queryFn: ({ signal, pageParam }) => {
        const query = new URLSearchParams({
          page: String(pageParam),
          limit: String(limit),
        });
        if (search) query.set("search", search);
        return api<CloudflareMcpServersPage>(
          `/api/admin/cloudflare/mcp/servers?${query.toString()}`,
          { signal },
        );
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        typeof lastPage.totalPages === "number"
          ? lastPage.page < lastPage.totalPages
            ? lastPage.page + 1
            : undefined
          : lastPage.servers.length === limit
            ? lastPage.page + 1
            : undefined,
      getPreviousPageParam: () => undefined,
      maxPages: 20,
      staleTime: 30 * 1000,
    }),
  cloudflareReusablePolicies: (limit = 20) =>
    infiniteQueryOptions({
      queryKey: adminQueryKeys.cloudflareReusablePoliciesInfinite(limit),
      queryFn: ({ signal, pageParam }) => {
        const query = new URLSearchParams({
          page: String(pageParam),
          limit: String(limit),
        });
        return api<CloudflareReusablePoliciesPage>(
          `/api/admin/cloudflare/reusable/policies?${query.toString()}`,
          { signal },
        );
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        typeof lastPage.totalPages === "number"
          ? lastPage.page < lastPage.totalPages
            ? lastPage.page + 1
            : undefined
          : lastPage.policies.length === limit
            ? lastPage.page + 1
            : undefined,
      getPreviousPageParam: () => undefined,
      maxPages: 20,
      staleTime: 60 * 1000,
    }),
  cloudflareAccessGroups: (limit = 20, search?: string) =>
    infiniteQueryOptions({
      queryKey: adminQueryKeys.cloudflareAccessGroupsInfinite(limit, search),
      queryFn: ({ signal, pageParam }) => {
        const query = new URLSearchParams({
          page: String(pageParam),
          limit: String(limit),
        });
        if (search) query.set("search", search);
        return api<CloudflareAccessGroupsPage>(
          `/api/admin/cloudflare/reusable/access-groups?${query.toString()}`,
          { signal },
        );
      },
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        typeof lastPage.totalPages === "number"
          ? lastPage.page < lastPage.totalPages
            ? lastPage.page + 1
            : undefined
          : lastPage.groups.length === limit
            ? lastPage.page + 1
            : undefined,
      getPreviousPageParam: () => undefined,
      maxPages: 20,
      staleTime: 60 * 1000,
    }),
  cloudflareZeroTrustLists: () =>
    queryOptions({
      queryKey: adminQueryKeys.cloudflareZeroTrustLists,
      queryFn: ({ signal }) =>
        api<CloudflareZeroTrustListsView>(
          "/api/admin/cloudflare/reusable/lists",
          { signal },
        ),
      staleTime: 60 * 1000,
    }),
};

/**
 * The session is fetched once by `AdminPage` and cached forever, so every panel
 * below that gate can read the identity, CSRF token, and password policy
 * without them being threaded through props.
 */
export function useAdminSession(): AdminSession {
  const session = useQuery(adminQueries.session());
  if (!session.data) {
    throw new Error("The admin session is not loaded yet.");
  }
  return session.data;
}

/** `api` with the session CSRF token attached, for mutating admin requests. */
export function useAdminApi(): <T>(
  path: string,
  init?: RequestInit,
) => Promise<T> {
  const { csrfToken } = useAdminSession();
  return useCallback(
    (path, init) =>
      api(path, {
        ...init,
        headers: { ...init?.headers, "X-CSRF-Token": csrfToken },
      }),
    [csrfToken],
  );
}

/**
 * Every Cloudflare mutation changes both the control-plane view and the
 * security timeline, so they all end with the same refresh.
 */
export function useRefreshCloudflare(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.cloudflare }),
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.security }),
    ]);
  }, [queryClient]);
}

export type CloudflareDevicePageSnapshot = Array<
  [QueryKey, InfiniteData<CloudflareDevicesPage, string> | undefined]
>;

export function optimisticallyRevokeCloudflareDevice(
  queryClient: QueryClient,
  deviceId: string,
): CloudflareDevicePageSnapshot {
  const snapshots = queryClient.getQueriesData<
    InfiniteData<CloudflareDevicesPage, string>
  >({ queryKey: adminQueryKeys.cloudflareDevices });
  for (const [key, current] of snapshots) {
    if (!current) continue;
    const filters = key.at(-1);
    const activeOnly =
      typeof filters === "object" &&
      filters !== null &&
      "status" in filters &&
      filters.status === "active";
    const includesDevice = current.pages.some((page) =>
      page.devices.some((device) => device.id === deviceId),
    );
    queryClient.setQueryData<InfiniteData<CloudflareDevicesPage, string>>(key, {
      ...current,
      pages: current.pages.map((page) => ({
        ...page,
        ...(activeOnly && includesDevice && typeof page.total === "number"
          ? { total: Math.max(0, page.total - 1) }
          : {}),
        devices: activeOnly
          ? page.devices.filter((device) => device.id !== deviceId)
          : page.devices.map((device) =>
              device.id === deviceId
                ? { ...device, activeRegistrations: 0 }
                : device,
            ),
      })),
    });
  }
  return snapshots;
}

export function restoreCloudflareDevicePages(
  queryClient: QueryClient,
  snapshots: CloudflareDevicePageSnapshot,
): void {
  for (const [key, snapshot] of snapshots) {
    queryClient.setQueryData(key, snapshot);
  }
}

type CloudflareMcpPortalSnapshot = Array<
  [QueryKey, InfiniteData<CloudflareMcpPortalsPage, number> | undefined]
>;
type CloudflareMcpServerSnapshot = Array<
  [QueryKey, InfiniteData<CloudflareMcpServersPage, number> | undefined]
>;

export interface CloudflareMcpPageSnapshots {
  portals: CloudflareMcpPortalSnapshot;
  servers: CloudflareMcpServerSnapshot;
}

function setCloudflareMcpServerStatus(
  queryClient: QueryClient,
  serverId: string,
  result: CloudflareMcpServerSyncResult,
): void {
  queryClient.setQueriesData<InfiniteData<CloudflareMcpServersPage, number>>(
    { queryKey: adminQueryKeys.cloudflareMcpServers },
    (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              servers: page.servers.map((server) =>
                server.id === serverId
                  ? {
                      ...server,
                      status: result.status,
                      error: result.error,
                      errorRetryable: result.errorRetryable,
                    }
                  : server,
              ),
            })),
          }
        : current,
  );
  queryClient.setQueriesData<InfiniteData<CloudflareMcpPortalsPage, number>>(
    { queryKey: adminQueryKeys.cloudflareMcpPortals },
    (current) =>
      current
        ? {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              portals: page.portals.map((portal) => ({
                ...portal,
                servers: portal.servers.map((server) =>
                  server.serverId === serverId
                    ? { ...server, status: result.status }
                    : server,
                ),
              })),
            })),
          }
        : current,
  );
}

export function optimisticallySyncCloudflareMcpServer(
  queryClient: QueryClient,
  serverId: string,
): CloudflareMcpPageSnapshots {
  const snapshots = {
    portals: queryClient.getQueriesData<
      InfiniteData<CloudflareMcpPortalsPage, number>
    >({ queryKey: adminQueryKeys.cloudflareMcpPortals }),
    servers: queryClient.getQueriesData<
      InfiniteData<CloudflareMcpServersPage, number>
    >({ queryKey: adminQueryKeys.cloudflareMcpServers }),
  };
  setCloudflareMcpServerStatus(queryClient, serverId, { status: "waiting" });
  return snapshots;
}

export function applyCloudflareMcpSyncResult(
  queryClient: QueryClient,
  serverId: string,
  result: CloudflareMcpServerSyncResult,
): void {
  setCloudflareMcpServerStatus(queryClient, serverId, result);
}

export function restoreCloudflareMcpPages(
  queryClient: QueryClient,
  snapshots: CloudflareMcpPageSnapshots,
): void {
  for (const [key, snapshot] of [...snapshots.portals, ...snapshots.servers]) {
    queryClient.setQueryData(key, snapshot);
  }
}

/** Replaces or appends `item`, keeping the list in its displayed order. */
export function upsertSorted<T>(
  items: T[],
  item: T,
  identity: (value: T) => string,
  order: (value: T) => string,
): T[] {
  const id = identity(item);
  const next = items.some((candidate) => identity(candidate) === id)
    ? items.map((candidate) => (identity(candidate) === id ? item : candidate))
    : [...items, item];
  return next.sort((left, right) => order(left).localeCompare(order(right)));
}

export function createAdminQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: true,
        retry: retryAdminQuery,
        staleTime: 30 * 1000,
      },
      mutations: { retry: false },
    },
  });
}

export type CloudflareOptimisticChange =
  | { type: "service-token-status"; id: string; enabled: boolean }
  | { type: "service-token-delete"; id: string }
  | { type: "service-token-upsert"; token: CloudflareServiceToken }
  | {
      type: "identity-provider-upsert";
      identityProvider: CloudflareIdentityProvider;
    }
  | {
      type: "scim-connection-upsert";
      connection: CloudflareScimConnection;
    }
  | { type: "user-sessions-revoke"; id: string };

export function optimisticallyUpdateCloudflareOverview(
  queryClient: QueryClient,
  change: CloudflareOptimisticChange,
): CloudflareOverview | undefined {
  const snapshot = queryClient.getQueryData<CloudflareOverview>(
    adminQueryKeys.cloudflare,
  );
  queryClient.setQueryData<CloudflareOverview>(
    adminQueryKeys.cloudflare,
    (current) => {
      if (!current) return current;
      if (change.type === "service-token-status") {
        return {
          ...current,
          serviceTokens: current.serviceTokens.map((token) =>
            token.id === change.id
              ? { ...token, enabled: change.enabled }
              : token,
          ),
        };
      }
      if (change.type === "service-token-delete") {
        return {
          ...current,
          serviceTokens: current.serviceTokens.filter(
            (token) => token.id !== change.id,
          ),
        };
      }
      if (change.type === "service-token-upsert") {
        return {
          ...current,
          serviceTokens: upsertSorted(
            current.serviceTokens,
            change.token,
            (token) => token.id,
            (token) => token.name,
          ),
        };
      }
      if (change.type === "identity-provider-upsert") {
        return {
          ...current,
          identityProviders: upsertSorted(
            current.identityProviders,
            change.identityProvider,
            (provider) => provider.id,
            (provider) => provider.name,
          ),
        };
      }
      if (change.type === "scim-connection-upsert") {
        return {
          ...current,
          scimConnections: upsertSorted(
            current.scimConnections,
            change.connection,
            (connection) => connection.identityProviderId,
            (connection) => connection.identityProviderName,
          ),
        };
      }
      return {
        ...current,
        sessions: current.sessions.map((user) =>
          user.id === change.id ? { ...user, sessions: [] } : user,
        ),
      };
    },
  );
  return snapshot;
}

export function restoreCloudflareOverview(
  queryClient: QueryClient,
  snapshot: CloudflareOverview,
): void {
  queryClient.setQueryData(adminQueryKeys.cloudflare, snapshot);
}
